import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';

// Types
export interface PartnerCommission {
  id: string;
  partner_user_id: string;
  period_start: string;
  period_end: string;
  subscription_revenue: number;
  boost_pack_revenue: number;
  total_revenue: number;
  commission_rate: number;
  commission_amount: number;
  stripe_fees: number;
  net_payout: number;
  status: 'estimated' | 'pending' | 'processing' | 'paid' | 'failed';
  stripe_payout_id?: string;
  paid_at?: string;
  notes?: string;
  created_at: string;
}

export interface CommissionSummary {
  total_commission: number;
  total_revenue: number;
  pending_commission: number;
  commission_count: number;
}

export interface CommissionOverview {
  commissions: PartnerCommission[];
  summary: CommissionSummary;
}

export interface CommissionCalculation {
  subscription_revenue: number;
  boost_pack_revenue: number;
  total_revenue: number;
  commission_rate: number;
  commission_amount: number;
  active_users: number;
}

export interface IndividualTransaction {
  id: string;
  date: string;
  customer_name: string;
  customer_email: string;
  type: string;
  amount: number;
  description: string;
}

export interface TransactionResponse {
  transactions: IndividualTransaction[];
  total_count: number;
  has_more: boolean;
}

// API functions
const fetchCommissionOverview = async (): Promise<CommissionOverview> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-commissions?action=overview`, {
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch commission overview');
  }

  return response.json();
};

// Deprecated manual calculation/generation endpoints removed (automatic at month close)

const fetchIndividualTransactions = async (limit: number = 50, offset: number = 0): Promise<TransactionResponse> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('No session');

  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/partner-commissions?action=transactions&limit=${limit}&offset=${offset}`,
    {
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch transactions');
  }

  return response.json();
};

// Hooks
export const usePartnerCommissions = () => {
  return useQuery({
    queryKey: ['partner', 'commissions'],
    queryFn: fetchCommissionOverview,
    staleTime: 3 * 60 * 1000, // 3 minutes - balance between fresh data and performance
  });
};

// Removed useCalculateCommission and useGenerateCommission hooks

// Helper hook for current month commission calculation
// Current month calculation now comes from the overview response; keep a thin helper if needed elsewhere
export const useCurrentMonthCommission = () => {
  // Reuse overview and compute current month client-side if needed in the component
  return usePartnerCommissions();
};

// Hook for individual transactions
export const useIndividualTransactions = (limit: number = 50, offset: number = 0) => {
  return useQuery({
    queryKey: ['partner', 'transactions', limit, offset],
    queryFn: () => fetchIndividualTransactions(limit, offset),
    staleTime: 2 * 60 * 1000, // 2 minutes - fresh transaction data
  });
};
